import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { AudienceLevel, Industry, SavedPathway } from '../../../types/pathway';
import { PathwayButton } from '../PathwayButton';
import { PathwayLibraryFilters } from '../PathwayLibraryFilters';
import { PathwayPagination } from '../PathwayPagination';
import { SavedPathwayRow } from './SavedPathwayRow';
import { matchesPathwayLibraryFilters, pathwayTopicOptions } from '../../../utils/pathwayLibrary';
const PAGE_SIZE = 5;
const SORT_OPTIONS = [{
  value: 'newest',
  label: 'Sort: Newest'
}, {
  value: 'name-asc',
  label: 'Sort: A–Z'
}, {
  value: 'name-desc',
  label: 'Sort: Z–A'
}];
interface SavedPathwaysViewProps {
  pathways: SavedPathway[];
  onOpen: (pathway: SavedPathway) => void;
  onDuplicate: (pathway: SavedPathway) => void;
  onBuildNew: () => void;
}
export function SavedPathwaysView({
  pathways,
  onOpen,
  onDuplicate,
  onBuildNew
}: SavedPathwaysViewProps) {
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState<Industry | 'all'>('all');
  const [role, setRole] = useState<AudienceLevel | 'all'>('all');
  const [topic, setTopic] = useState('all');
  const [sort, setSort] = useState('newest');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const topics = useMemo(() => pathwayTopicOptions(pathways.map(pathway => pathway.moduleIds)), [pathways]);
  const visible = useMemo(() => {
    const filtered = pathways.filter(pathway => matchesPathwayLibraryFilters(pathway.moduleIds, pathway.audience, `${pathway.name} ${pathway.organization}`, {
      query,
      industry,
      role,
      topic
    }));
    return filtered.slice().sort((a, b) => {
      if (sort === 'name-asc') return a.name.localeCompare(b.name);
      if (sort === 'name-desc') return b.name.localeCompare(a.name);
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [pathways, query, industry, role, topic, sort]);
  const isFiltered = query.trim().length > 0 || industry !== 'all' || role !== 'all' || topic !== 'all' || sort !== 'newest';
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const update = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };
  const resetSearch = () => {
    setQuery('');
    setIndustry('all');
    setRole('all');
    setTopic('all');
    setSort('newest');
    setPage(1);
  };
  return <section aria-labelledby="saved-pathways-heading" data-id="element-1951" data-mp="2nn4v9mtzsnu0">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4" data-id="element-1952" data-mp="2ipl6vu3kyuoc">
        <div data-id="element-1953" data-mp="2inijzixq5hnu">
          <h2 id="saved-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1954" data-mp="3g4qq3qjej9ss">
            Your pathways{pathways.length > 0 ? ` (${pathways.length})` : ''}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1955" data-mp="3bbxo3afbifke">
            Pick up something you started, or duplicate a pathway that worked for a new prospect.
          </p>
        </div>
        <PathwayButton onClick={onBuildNew} className="whitespace-nowrap px-8" data-id="element-1956" data-mp="3noi3x3oq4t0u">
          <Plus size={18} data-id="element-1957" data-mp="3smjjvfqusc83" />
          Build a New Pathway
        </PathwayButton>
      </div>

      {pathways.length > 0 && <PathwayLibraryFilters searchLabel="Search your pathways" searchPlaceholder="Search by name or company" query={query} industry={industry} role={role} topic={topic} topics={topics} sort={sort} sortOptions={SORT_OPTIONS} isActive={isFiltered} onQueryChange={update(setQuery)} onIndustryChange={update(setIndustry)} onRoleChange={update(setRole)} onTopicChange={update(setTopic)} onSortChange={update(setSort)} onReset={resetSearch} data-id="element-1958" data-mp="hfrjxzwczy7" />}

      {pathways.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1959" data-mp="fkpdyrxj7rmc">
          <h3 className="text-base font-bold text-gray-900" data-id="element-1960" data-mp="biubvl7s1a7i">No pathways yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-600" data-id="element-1961" data-mp="pbtptirwuwcy">
            Build one from a conversation you just had, or start from a proven pathway below.
          </p>
        </div> : visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1962" data-mp="3sssde2uk92w6">
          <p className="text-sm text-gray-600" data-id="element-1963" data-mp="3orqmqaeplz5t">No pathways match those filters.</p>
          <button type="button" onClick={resetSearch} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1964" data-mp="6l2h8jfqz9zm">
            Reset search
          </button>
        </div> : <div data-id="element-1965" data-mp="1d0v3bkfloxp3">
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1966" data-mp="1d14rr3kzywso">
            <div className="overflow-x-auto" data-id="element-1967" data-mp="2uyrdsod3ski1">
              <table className="w-full table-fixed sm:min-w-[760px]" data-id="element-1968" data-mp="2uwy7ja6ek2ak">
                <caption className="sr-only" data-id="element-1969" data-mp="2zxuiu8t32qq0">Saved pathways — expand a row to see its modules</caption>
                <colgroup data-id="element-1970" data-mp="2lyuwj7t1mnx0"><col data-id="element-1971" data-mp="2lz4o2ztql34t" /><col className="w-[200px]" data-id="element-1972" data-mp="2lz4lcs2cqp14" /><col className="w-[110px]" data-id="element-1973" data-mp="2lzwapl5cvqiy" /><col className="w-[190px]" data-id="element-1974" data-mp="2qwmm3v67txy1" /></colgroup>
                <thead data-id="element-1975" data-mp="2qx675kr5x2ga"><tr className="border-b border-gray-200 bg-gray-50" data-id="element-1976" data-mp="2qx63naryekbj">
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1977" data-mp="3ens2cbg2is8v">Pathway</th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1978" data-mp="3inaqdg36rjfx">Audience</th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1979" data-mp="39vs4qpnh0bfg">Modules</th>
                  <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1980" data-mp="1exficzlipju"><span className="sr-only" data-id="element-1981" data-mp="66xd3ys716d9">Actions</span></th>
                </tr></thead>
                <tbody data-id="element-1982" data-mp="g2xgu93qg0ep">
                  {pageItems.map(pathway => <SavedPathwayRow key={pathway.id} pathway={pathway} expanded={expandedId === pathway.id} onToggleExpand={() => setExpandedId(expandedId === pathway.id ? null : pathway.id)} onOpen={onOpen} onDuplicate={onDuplicate} data-id="element-1983" data-mp="b3kstl14knaj" />)}
                </tbody>
              </table>
            </div>
          </div>
          <PathwayPagination label="Your pathways pages" page={safePage} pageCount={pageCount} onChange={setPage} data-id="element-1984" data-mp="3ogmjpead4yia" />
        </div>}
    </section>;
}